/* eslint no-undef: "off" */

import { getCart } from './helpers'
import {
  GtmItem,
  GtmUser,
  GtmCart,
  GtmCollection,
  GtmSearch,
  GtmProduct,
  getGProductId,
} from './models'
import { FAVORITE_OPERATIONS } from '~/js/services/favouriteService'

const ID_DATA_GTM = 'js-data-gtm'

window.dataLayer = window.dataLayer || []

document.addEventListener('DOMContentLoaded', async () => {
  const elementDataGtm = document.getElementById(ID_DATA_GTM)
  if (!elementDataGtm) return

  const gtmItem = new GtmItem(elementDataGtm.dataset)
  const user = new GtmUser(elementDataGtm.dataset)
  const collection = new GtmCollection(elementDataGtm.dataset)
  const search = new GtmSearch(elementDataGtm.dataset)
  const product = new GtmProduct(elementDataGtm.dataset)

  const datasetCart = JSON.parse(elementDataGtm.dataset.cart)
  const cartProductCategories = (
    elementDataGtm.dataset.cartProductCategories || ''
  ).split(',')
  const cartProductCategoryIds = (
    elementDataGtm.dataset.cartProductCategoryIds || ''
  ).split(',')
  const items = datasetCart.items.map((item, index) => {
    return {
      ...item,
      category: cartProductCategories[index],
      categoryId: cartProductCategoryIds[index],
      url: `${gtmItem.shopUrl}${item.url}`,
    }
  })
  const cart = new GtmCart({ ...datasetCart, items }, gtmItem.shopUrl)

  pushStartEvent({ gtmItem, cart, user })

  switch (gtmItem.templateName) {
    case 'collection':
      pushCollectionPageEvent({ collection })
      break
    case 'search':
      pushSearchPageEvent({ gtmItem, cart, search })
      break
    case 'product':
      pushProductPageEvent({ gtmItem, cart, product })
      break
    case 'cart':
      pushCartPageEvent({ gtmItem, cart })
      break
  }
})

const pushStartEvent = ({ gtmItem, cart, user }) => {
  if (dataLayer.find((dl) => dl.event === 'sh_info')) return
  window.dataLayer.push({
    event: 'sh_info',
    page_type: gtmItem.pageType,
    cart_total_price: cart.totalPrice,
    cart_total_quantity: cart.totalQuantity,
    cart_status: cart.cartStatus,
    page_currency: gtmItem.currency,
    ...user,
  })
}

const pushCollectionPageEvent = ({ collection }) => {
  if (dataLayer.find((dl) => dl.event === 'ee_productImpression')) return
  window.dataLayer.push({
    event: 'ee_productImpression',
    category_product_sku: collection.products.skus,
    category_product_position: collection.products.positions,
  })
}

const pushSearchPageEvent = ({ gtmItem, cart, search }) => {
  if (dataLayer.find((dl) => dl.event === 'searchListInfo')) return
  window.dataLayer.push({
    event: 'searchListInfo',
    page_type: gtmItem.pageType,
    cart_status: cart.cartStatus,
    category_product_brand: search.products.vendors,
    category_product_type: search.products.types,
    category_product_sku: search.products.skus,
    category_product_name: search.products.titles,
    category_product_id: search.products.ids,
    category_product_price: search.products.prices,
    category_product_position: search.products.positions,
    currency: gtmItem.currency,
    search_term: search.term,
    search_results: search.resultsCount,
    g_product_id: search.products.gProductIds,
    variant_id: search.products.variantIds,
  })
}

const pushProductPageEvent = ({ gtmItem, cart, product }) => {
  if (dataLayer.find((dl) => dl.event === 'ee_productDetail')) return
  window.dataLayer.push({
    event: 'ee_productDetail',
    name: product.title,
    brand: product.brand,
    category: product.category,
    cart_status: cart.cartStatus,
    cart_total_price: cart.totalPrice,
    cart_total_quantity: cart.totalQuantity,
    currency: gtmItem.currency,
    g_product_id: product.gProductId,
    item_id: product.id,
    item_image_url: product.featuredImageUrl,
    item_url: product.itemUrl,
    price: product.price,
    product_type: product.type,
    sku: product.sku,
    variant_id: product.variantId,
  })
}

const pushCartPageEvent = ({ gtmItem, cart }) => {
  if (dataLayer.find((dl) => dl.event === 'ee_checkout')) return
  window.dataLayer.push({
    event: 'ee_checkout',
    page_type: gtmItem.pageType,
    brand: cart.itemArrays.brands,
    category: cart.itemArrays.categories,
    category_id: cart.itemArrays.categoryIds,
    currency: gtmItem.currency,
    item_names: cart.itemArrays.names,
    item_ids: cart.itemArrays.ids,
    item_image_urls: cart.itemArrays.imageUrls,
    item_prices: cart.itemArrays.prices,
    item_urls: cart.itemArrays.urls,
    item_quantities: cart.itemArrays.quantities,
    items: cart.items,
    product_type: cart.itemArrays.types,
    sku: cart.itemArrays.skus,
    status: cart.cartStatus,
    totalQuantity: cart.totalQuantity,
    totalValue: cart.totalPrice,
    variant: cart.itemArrays.variantTitles,
    variant_id: cart.itemArrays.variantIds,
  })
}

export const pushFavoriteEvent = ({
  type,
  handle,
  operation = FAVORITE_OPERATIONS.ADD,
}) => {
  window.dataLayer.push({
    event: 'favorite',
    type,
    handle,
    operation,
  })
}

export const pushProductClickEvent = async ({ product, position }) => {
  const elementDataGtm = document.getElementById(ID_DATA_GTM)
  if (!elementDataGtm) return

  const gtmItem = new GtmItem(elementDataGtm.dataset)
  if (
    gtmItem.templateName !== 'collection' &&
    gtmItem.templateName !== 'search'
  )
    return

  const cart = new GtmCart(await getCart())
  const collection = new GtmCollection(elementDataGtm.dataset)

  const dataLayer = {
    event: 'ee_productClick',
    cart_status: cart.cartStatus,
    currency: gtmItem.currency,
    product_brand: product.vendor,
    product_id: product.id.toString(),
    product_name: product.title,
    product_position: position,
    product_price: product.price.toString(),
    product_type: product.productType,
    sku: product.handle,
    variant_id: product.variantId,
  }
  if (collection) {
    dataLayer.category_name = collection.title
    dataLayer.category_id = collection.id
  }
  window.dataLayer.push(dataLayer)
}

export const pushCartAddedEvent = async ({ items }) => {
  const elementDataGtm = document.getElementById(ID_DATA_GTM)
  if (!elementDataGtm) return

  const gtmItem = new GtmItem(elementDataGtm.dataset)
  const cart = new GtmCart(await getCart())
  items.forEach((item) => {
    window.dataLayer.push({
      event: 'ee_addToCart',
      name: item.productTitle,
      id: item.id,
      price: item.price,
      cart_status: cart.cartStatus,
      cart_total_price: cart.totalPrice,
      cart_total_quantity: cart.totalQuantity,
      currency: gtmItem.currency,
      brand: item.vendor,
      product_type: item.productType,
      category: item.category || '',
      quantity: item.addedQuantity,
      variant: item.variant,
      g_product_id: getGProductId(item.id, item.variant),
    })
  })
}

export const pushItemRemovedEvent = async ({ itemRemoved, quantity }) => {
  const elementDataGtm = document.getElementById(ID_DATA_GTM)
  if (!elementDataGtm) return

  const gtmItem = new GtmItem(elementDataGtm.dataset)
  const cart = new GtmCart(await getCart())

  window.dataLayer.push({
    event: 'ee_removeFromCart',
    name: itemRemoved.title,
    id: itemRemoved.id.toString(),
    variant: itemRemoved.variants?.[0]?.id?.toString() || '',
    price: Number((itemRemoved.price / 100).toFixed(2)),
    currency: gtmItem.currency,
    brand: itemRemoved.vendor,
    quantity,
    cart_status: cart.cartStatus,
  })
}
